import type { JSX, ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import type { Slot } from '@/features/scheduling';

/**
 * The booking draft, held for the whole of §6's five-step journey.
 *
 * It lives in one place rather than in each step because the steps are five **routes**: a
 * back button between them must not lose the basket. Nothing here is persisted — a draft
 * is not a booking, and a half-finished one left in `localStorage` would be a record of
 * someone's intentions that nobody asked us to keep (BP-03's discipline).
 */
export interface BookingDraft {
  serviceIds: string[];
  addonIds: string[];
  staffId: string | null;
  date: string;
  slot: Slot | null;
  phone: string;
  consentReminders: boolean;
  clientId: string | null;
  /** BP-21: set when the journey was started from "book this look". */
  preselectedSkuId: string | null;
}

/** An empty draft for `today`, optionally carrying BP-21's shade. */
export function emptyDraft(today: string, preselectedSkuId: string | null = null): BookingDraft {
  return {
    serviceIds: [],
    addonIds: [],
    staffId: null,
    date: today,
    slot: null,
    phone: '',
    consentReminders: false,
    preselectedSkuId,
    clientId: null,
  };
}

/** Toggle `id` in a list, keeping the order it was chosen in. */
export function toggle(chosen: readonly string[], id: string): string[] {
  return chosen.includes(id) ? chosen.filter((each) => each !== id) : [...chosen, id];
}

export interface Journey {
  draft: BookingDraft;
  patch: (change: Partial<BookingDraft>) => void;
  reset: () => void;
}

const JourneyContext = createContext<Journey | null>(null);

export function JourneyProvider({
  today,
  preselectedSkuId = null,
  children,
}: {
  today: string;
  /**
   * BP-21 "book this look": the shade the try-on screen sent her here with.
   *
   * Seeded into the draft rather than patched in by the first step, so a
   * client who lands on `/book/slot` directly — from a back button, from a
   * restored tab — still books the shade she chose. `reset` keeps it for the
   * same reason: starting over within the journey is changing her mind
   * about the service, not about the colour.
   */
  preselectedSkuId?: string | null;
  children: ReactNode;
}): JSX.Element {
  const [draft, setDraft] = useState(() => emptyDraft(today, preselectedSkuId));
  const value = useMemo<Journey>(
    () => ({
      draft,
      patch: (change) => {
        setDraft((current) => ({ ...current, ...change }));
      },
      reset: () => {
        setDraft(emptyDraft(today, preselectedSkuId));
      },
    }),
    [draft, today, preselectedSkuId],
  );
  return <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>;
}

/** The draft. Throws when a step is rendered outside the journey. */
export function useJourney(): Journey {
  const journey = useContext(JourneyContext);
  if (journey === null) {
    throw new Error('26AD booking: this step must be rendered inside <JourneyProvider>');
  }
  return journey;
}
